import React, { useEffect, useState } from "react";
import { Toaster } from "sonner";
import { motion } from "framer-motion";
import { LayoutDashboard, Ticket, Mail, Users, MessageSquareHeart, ArrowLeft } from "lucide-react";
import { adminStats } from "@/lib/api";
import OverviewSection from "./OverviewSection";
import CodesSection from "./CodesSection";
import AllowlistSection from "./AllowlistSection";
import BetaUsersSection from "./BetaUsersSection";
import FeedbackSection from "./FeedbackSection";

const TABS = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "codes", label: "Invite codes", icon: Ticket },
    { id: "allowlist", label: "Allowlist", icon: Mail },
    { id: "users", label: "Beta users", icon: Users },
    { id: "feedback", label: "Feedback", icon: MessageSquareHeart },
];

function NotFound() {
    return (
        <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" data-testid="admin-not-found">
            <div className="text-6xl mb-4">🍡</div>
            <h1 className="font-display text-2xl text-warm-900 mb-2">This page doesn't exist</h1>
            <p className="text-warm-500 text-sm mb-6">
                nothing to see here… maybe Mochi wandered off?
            </p>
            <a
                href="/"
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-cream bg-terracotta hover:bg-terracotta-hover transition-colors"
                data-testid="admin-not-found-home"
            >
                Take me home
            </a>
        </div>
    );
}

export default function AdminPanel() {
    const [state, setState] = useState("checking"); // checking | ok | denied
    const [tab, setTab] = useState("overview");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await adminStats();
                if (!cancelled) setState("ok");
            } catch {
                if (!cancelled) setState("denied");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (state === "checking") {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="admin-checking">
                <p className="font-display text-warm-500 text-sm animate-soft-pulse">peeking behind the curtain… 🍵</p>
            </div>
        );
    }
    if (state === "denied") return <NotFound />;

    return (
        <div className="min-h-screen font-body pb-16" data-testid="admin-panel">
            <Toaster position="top-center" richColors={false} />

            {/* Header */}
            <header className="border-b border-warm-300/30 bg-cream/70 backdrop-blur-md sticky top-0 z-20">
                <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="text-2xl">🍡</span>
                        <div>
                            <h1 className="font-display text-warm-900 text-lg leading-none">Mochi · Beta Access</h1>
                            <p className="text-warm-500 text-xs mt-0.5">cozy admin</p>
                        </div>
                    </div>
                    <a
                        href="/"
                        className="flex items-center gap-1.5 text-warm-500 hover:text-warm-900 text-sm transition-colors"
                        data-testid="admin-back-to-app"
                    >
                        <ArrowLeft className="w-4 h-4" /> back to Mochi
                    </a>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-5 pt-6">
                {/* Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-1" data-testid="admin-tabs">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                className={`relative flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${active ? "text-cream" : "text-warm-800 hover:bg-surface"}`}
                                data-testid={`admin-tab-${t.id}`}
                            >
                                {active && (
                                    <motion.div
                                        layoutId="admin-tab-bg"
                                        className="absolute inset-0 bg-terracotta rounded-full"
                                        transition={{ type: "spring", stiffness: 300, damping: 26 }}
                                    />
                                )}
                                <span className="relative flex items-center gap-2">
                                    <Icon className="w-4 h-4" /> {t.label}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Sections */}
                <motion.div
                    key={tab}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.22 }}
                >
                    {tab === "overview" && <OverviewSection />}
                    {tab === "codes" && <CodesSection />}
                    {tab === "allowlist" && <AllowlistSection />}
                    {tab === "users" && <BetaUsersSection />}
                    {tab === "feedback" && <FeedbackSection />}
                </motion.div>
            </div>
        </div>
    );
}
